// Progression engine — XP, levels, ranks, and the World Completion Engine.
// Built on top of /lib/progress.js (localStorage) and dispatches `we-celebrate`
// CustomEvents that <CelebrationOverlay /> listens to.

import { loadProgress, saveProgress, updateStreak } from "@/lib/progress";
import { trackEvent } from "@/lib/analytics";
import { COUNTRIES } from "@/data/countries";

// XP table — single source of truth for all activity rewards.
// Keep tuned so an engaged kid hits "Level 2" on the first day,
// "Level 5" within a week, and "Level 20" only after they've
// genuinely explored the whole world.
export const XP = {
  LEARN_COUNTRY: 20,
  QUIZ_COMPLETED: 30,
  PUZZLE_COMPLETED: 25,
  DAILY_CHALLENGE: 50,
  LANDMARK_COLORING: 15,
  MATH_GEO: 35,
  MEMORY_MATCH: 20,
  HEROES_GAME: 25,
  CONTINENT_COMPLETE: 150,
  OUTCOME_PASSED: 40,       // 4/5 correct on a curriculum-outcome test
  OUTCOME_MASTERED: 200,    // crossed the 3-strong-attempts mastery bar
};

// Levels — cumulative XP threshold + display name. Add more later if needed;
// `levelForXp` clamps to the last entry, so this list can grow safely.
export const LEVELS = [
  { level: 1,  minXp: 0,     title: "Little Sprout" },
  { level: 2,  minXp: 100,   title: "Curious Kid" },
  { level: 3,  minXp: 250,   title: "Bright Star" },
  { level: 4,  minXp: 500,   title: "Path Finder" },
  { level: 5,  minXp: 1000,  title: "World Trekker" },
  { level: 6,  minXp: 1500,  title: "Sky Traveller" },
  { level: 7,  minXp: 2200,  title: "Star Explorer" },
  { level: 8,  minXp: 3000,  title: "Map Master" },
  { level: 9,  minXp: 4000,  title: "Big Voyager" },
  { level: 10, minXp: 5000,  title: "Champion" },
  { level: 12, minXp: 7000,  title: "Super Explorer" },
  { level: 15, minXp: 10000, title: "Wise Guide" },
  { level: 20, minXp: 20000, title: "World Hero" },
];

// Explorer rank — based on countries learned (separate from XP/level).
export const RANKS = [
  { min: 0,  title: "Little Sprout",   emoji: "🌱" },
  { min: 5,  title: "Happy Explorer",  emoji: "🧭" },
  { min: 15, title: "World Traveller", emoji: "✈️" },
  { min: 30, title: "Adventure Star",  emoji: "🗺️" },
  { min: 50, title: "Global Hero",     emoji: "🌟" },
];

// All countries except Antarctica count toward world completion.
export const TOTAL_COUNTRIES = COUNTRIES.filter((c) => c.code !== "aq").length;

export function levelForXp(xp) {
  let current = LEVELS[0];
  let next = LEVELS[1] || null;
  for (let i = 0; i < LEVELS.length; i++) {
    if (xp >= LEVELS[i].minXp) {
      current = LEVELS[i];
      next = LEVELS[i + 1] || null;
    } else break;
  }
  const into = xp - current.minXp;
  const span = next ? next.minXp - current.minXp : 1;
  const pct = next ? Math.min(100, Math.round((into / span) * 100)) : 100;
  return { ...current, next, into, span, pct };
}

export function rankForCount(n) {
  let r = RANKS[0];
  for (const item of RANKS) if (n >= item.min) r = item;
  return r;
}

export function worldCompletion(p = loadProgress()) {
  const visited = (p.countriesLearned || []).filter((c) => c !== "aq").length;
  const pct = Math.min(100, Math.round((visited / TOTAL_COUNTRIES) * 100));
  return { visited, total: TOTAL_COUNTRIES, pct };
}

// Continent → count of spotlight countries in that continent (excluding aq).
const CONTINENT_TOTALS = COUNTRIES
  .filter((c) => c.code !== "aq")
  .reduce((m, c) => ((m[c.continent] = (m[c.continent] || 0) + 1), m), {});

export function continentProgress(p = loadProgress()) {
  const learned = new Set(p.countriesLearned || []);
  return Object.entries(CONTINENT_TOTALS).map(([cid, total]) => {
    const got = COUNTRIES.filter((c) => c.continent === cid && learned.has(c.code)).length;
    return { id: cid, learned: got, total, pct: Math.round((got / total) * 100) };
  });
}

function celebrate(kind, payload = {}) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("we-celebrate", { detail: { kind, ...payload } }));
}

/**
 * Award XP for an activity. Updates streak, fires `we-celebrate` on level up.
 * Returns the new progress state.
 */
export function grantXp(amount, reason = "") {
  if (!amount || amount <= 0) return loadProgress();
  const p = loadProgress();
  const prevLevel = levelForXp(p.xp || 0).level;
  p.xp = (p.xp || 0) + amount;
  p.activity = p.activity || [];
  p.activity.unshift({ date: new Date().toISOString(), type: "xp", detail: `+${amount} XP ${reason}`.trim() });
  p.activity = p.activity.slice(0, 50);
  updateStreak(p);
  saveProgress(p);
  const newLevel = levelForXp(p.xp).level;
  if (newLevel > prevLevel) {
    const meta = levelForXp(p.xp);
    celebrate("levelup", { level: newLevel, title: meta.title });
    try { trackEvent("level_up", { level: newLevel, title: meta.title, xp: p.xp }); } catch {/* ignore */}
  }
  return p;
}

/**
 * Record a country as learned. Idempotent. Stores a "stamp" with metadata
 * (date, fact, continent) for the Passport page. Awards XP + a star.
 */
export function stampCountry(code, { fact, continent } = {}) {
  if (!code) return loadProgress();
  const p = loadProgress();
  p.countriesLearned = p.countriesLearned || [];
  p.stamps = p.stamps || {};
  const already = p.countriesLearned.includes(code);
  if (!already) {
    p.countriesLearned.push(code);
    p.stamps[code] = {
      date: new Date().toISOString(),
      fact: fact || "",
      continent: continent || "",
    };
    p.stars = (p.stars || 0) + 3;
    p.activity = p.activity || [];
    p.activity.unshift({ date: new Date().toISOString(), type: "stamp", detail: `🌍 New stamp: ${code.toUpperCase()}` });
    p.activity = p.activity.slice(0, 50);
  }
  updateStreak(p);
  saveProgress(p);
  if (!already) {
    grantXp(XP.LEARN_COUNTRY, `learned ${code.toUpperCase()}`);
    // Continent-completion bonus
    const cp = continentProgress();
    const justCompleted = cp.find((x) => x.id === continent && x.learned === x.total && x.total > 0);
    if (justCompleted) {
      grantXp(XP.CONTINENT_COMPLETE, `${continent} cleared`);
      celebrate("continent", { continent });
      try { trackEvent("continent_completed", { continent }); } catch {/* ignore */}
    }
    celebrate("stamp", { code });
    // GA4 event — one of the biggest kid-facing conversions.
    try { trackEvent("country_stamped", { code, continent: continent || "", total: p.countriesLearned.length, streak: p.streak }); } catch {/* ignore */}
  }
  return loadProgress();
}

/** Fire a generic celebration (used by quiz/streak milestones, etc.) */
export function fireCelebration(kind, payload = {}) {
  celebrate(kind, payload);
}
